import React, {createContext, useReducer} from 'react';
import {gaEvent} from 'app/utilities/analytics/tracking';
import gaParams from 'app/utilities/analytics/constants';

type GaActionType = {category: string; eventType: string; eventParams: string | any[]};

const gaAction = (param: GaActionType, params: string = null) =>
  param && gaEvent(param.category, param.eventType, params ?? param.eventParams);

export type BussinessOrg = {
  bussinesContactRole: {
    companyPsidList: unknown[];
    hasPayingEntity: boolean;
    isActionAllowedCellcom: boolean;
    isActionAllowedNv: boolean;
    isActionAllowedPayingNv: boolean;
  };
  cellInd: boolean;
  netvInd: boolean;
  contactRoleId: number;
  idNumber: string;
  orgId: string;
  orgName: string;
  portalList: string[];
  showScActions: boolean;
};

export enum InvoiceOrderByEnum {
  InvoiceNumber = 1,
  PaymentDateDesc = 2,
  Amount = 32,
  InvoiceNumberDesc = 8,
  PaymentDate = 16,
  AmountDesc = 4,
}

const InvoiceOrderByMap: {[idx: number]: GaActionType} = {
  [InvoiceOrderByEnum.Amount]: gaParams.INVOICE_VIEWING_CLICK_SORT_AMOUNT,
  [InvoiceOrderByEnum.AmountDesc]: gaParams.INVOICE_VIEWING_CLICK_SORT_AMOUNT,
  [InvoiceOrderByEnum.InvoiceNumber]: gaParams.INVOICE_VIEWING_CLICK_SORT_INVOICENUMBER,
  [InvoiceOrderByEnum.InvoiceNumberDesc]: gaParams.INVOICE_VIEWING_CLICK_SORT_INVOICENUMBER,
  [InvoiceOrderByEnum.PaymentDate]: gaParams.INVOICE_VIEWING_CLICK_SORT_PAYMENTDATE,
  [InvoiceOrderByEnum.PaymentDateDesc]: gaParams.INVOICE_VIEWING_CLICK_SORT_PAYMENTDATE,
};

export type CompanyInvoiceSearch = {
  invoiceOrigin: number;
  invoiceCount: number;
  filterByBanPsid: boolean;
  filterBySubscriber: boolean;
  filterValue: string;
};

export type ContactCompanyList = {
  bussinessOrgList: BussinessOrg[];
  contactFirstName: string;
  contactId: string;
  contactLastName: string;
  contactPhoneNumber: string;
};

export type CompanyInvoice = {
  invoiceAccountType: string;
  invoiceAmount: number;
  invoiceFullDate: string;
  invoiceNumber: string;
  paymentDate: string;
  accountNumber: string;
  checked: boolean;
  invoiceGuid: string;
};

export enum AccountType {
  Cellcom = 0,
  Netvision = 1,
}

export const InvoiceCountMap: {[month: number]: GaActionType} = {
  1: gaParams.INVOICE_VIEWING_CLICK_SEARCH_MONTH_1,
  3: gaParams.INVOICE_VIEWING_CLICK_SEARCH_MONTH_3,
  6: gaParams.INVOICE_VIEWING_CLICK_SEARCH_MONTH_6,
  12: gaParams.INVOICE_VIEWING_CLICK_SEARCH_MONTH_12,
  24: gaParams.INVOICE_VIEWING_CLICK_SEARCH_MONTH_24,
  36: gaParams.INVOICE_VIEWING_CLICK_SEARCH_MONTH_36,
};

export const InvoiceCountList = Object.keys(InvoiceCountMap).map((d) => +d);

export enum AccountTypeName {
  Cellcom = 'סלקום',
  Netvision = `נטויז'ן`,
  Golan = 'גולן טלקום'
}

type State = {
  loader: boolean;
  list: ContactCompanyList | null;
  selected: BussinessOrg | null;
  search: CompanyInvoiceSearch | null;
  orderBy: InvoiceOrderByEnum;
  pdf: {
    invoice: CompanyInvoice;
    companyId: string;
  } | null;
  invoices: CompanyInvoice[] | null;
  checked: CompanyInvoice[] | null;
  email: CompanyInvoice[] | null;
  empty: boolean;
  collapsed: boolean;
  views: number;
  timeout: boolean;
};

type Action =
  | {type: 'init'; list: ContactCompanyList; selectedFromPortal: string}
  | {type: 'select'; item: BussinessOrg}
  | {type: 'search'; search: CompanyInvoiceSearch | null}
  | {type: 'order'; orderBy: InvoiceOrderByEnum}
  | {type: 'loader'}
  | {type: 'collapsed'; collapsed: boolean}
  | {type: 'results'; data: CompanyInvoice[]}
  | {type: 'checked'}
  | {type: 'open-pdf'; invoice: CompanyInvoice}
  | {type: 'open-pdf-view'}
  | {type: 'close-pdf'}
  | {type: 'send-email'; status: 'SEND' | 'SUCCESS' | 'FAILURE'; count: number}
  | {type: 'open-email'; invoices: CompanyInvoice[] | null}
  | {type: 'close-email'}
  | {type: 'open-timeout'}
  | {type: 'close-timeout'}
  | {type: 'faq'; status: 'show' | 'button' | 'item'};

export const initialState: State = {
  loader: false,
  list: null,
  selected: null,
  search: null,
  orderBy: InvoiceOrderByEnum.InvoiceNumberDesc,
  invoices: null,
  checked: null,
  pdf: null,
  email: null,
  empty: false,
  collapsed: false,
  views: 0,
  timeout: false,
};

const reducer = (state: State, action: Action): State => {
  const selected = state.selected;
  switch (action.type) {
    case 'init':
      gaAction(gaParams.INVOICE_VIEWING_INIT);
      const selected =
        action.list?.bussinessOrgList.length == 1
          ? action.list?.bussinessOrgList[0]
          : action.list?.bussinessOrgList.find((d) => d.idNumber == action.selectedFromPortal) ?? null;
      state = {...state, list: action.list, selected};
      break;
    case 'select':
      if (action.item !== state.selected) {
        gaAction(gaParams.INVOICE_VIEWING_CLICK_SELECT, state.list.bussinessOrgList.length.toString());
        state = {...state, selected: action.item, search: null, invoices: null, empty: false, checked: null};
      }
      break;
    case 'loader':
      state = {...state, loader: true};
      break;
    case 'collapsed':
      gaAction(action.collapsed ? gaParams.INVOICE_VIEWING_SEARCH_COLLAPSE : gaParams.INVOICE_VIEWING_SEARCH_EXPAND);
      state = {...state, collapsed: action.collapsed};
      break;
    case 'search':
      if (action.search == null) {
        gaAction(gaParams.INVOICE_VIEWING_SEARCH_NOT_VALID);
      } else {
        // analytics
        if (action.search.invoiceOrigin == 2) {
          gaAction(gaParams.INVOICE_VIEWING_CLICK_SEARCH_CELLCOM);
          gaAction(gaParams.INVOICE_VIEWING_CLICK_SEARCH_NETVISION);
          gaAction(gaParams.INVOICE_VIEWING_CLICK_SEARCH_BOTH);
        } else {
          action.search.invoiceOrigin == 0 && gaAction(gaParams.INVOICE_VIEWING_CLICK_SEARCH_CELLCOM);
          action.search.invoiceOrigin == 1 && gaAction(gaParams.INVOICE_VIEWING_CLICK_SEARCH_NETVISION);
        }

        gaAction(InvoiceCountMap[action.search.invoiceCount]);
        action.search.filterByBanPsid && gaAction(gaParams.INVOICE_VIEWING_CLICK_SEARCH_FILTER_PSID);
        action.search.filterBySubscriber && gaAction(gaParams.INVOICE_VIEWING_CLICK_SEARCH_FILTER_SUBSCRIBER);
        gaAction(gaParams.INVOICE_VIEWING_CLICK_SEARCH);
        state = {...state, search: action.search, orderBy: InvoiceOrderByEnum.InvoiceNumberDesc};
      }
      break;
    case 'order':
      gaAction(InvoiceOrderByMap[action.orderBy]);
      gaAction(action.orderBy < 5 ? gaParams.INVOICE_VIEWING_CLICK_SORT_ASC : gaParams.INVOICE_VIEWING_CLICK_SORT_DESC);
      state = {...state, orderBy: action.orderBy};
      break;
    case 'results':
      const empty = action.data == null || action.data.length === 0;
      const invoices = empty ? null : action.data;
      let checked: CompanyInvoice[] = null;
      if (!empty) {
        gaAction(gaParams.INVOICE_VIEWING_SEARCH_RESULTS);
        gaAction(gaParams.INVOICE_VIEWING_CLICK_SEARCH_RESULTS);
        const checkedSet = new Set(state.checked?.map((d) => d.invoiceNumber));
        invoices?.forEach((invoice) => (invoice.checked = checkedSet.has(invoice.invoiceNumber)));
        checked = invoices.filter((invoice) => !!invoice.checked);
      } else {
        gaAction(gaParams.INVOICE_VIEWING_SEARCH_EMPTY);
      }
      state = {...state, invoices, checked, loader: false, empty, collapsed: !empty};
      break;
    case 'checked':
      state = {...state, checked: state.invoices.filter((d) => !!d.checked)};
      break;
    case 'open-pdf':
      gaAction(gaParams.INVOICE_VIEWING_CLICK_OPEN_PDF);
      state = {...state, pdf: {invoice: action.invoice, companyId: state.selected.idNumber}};
      break;
    case 'open-pdf-view':
      state = {...state, views: state.views + 1};
      gaAction(gaParams.INVOICE_VIEWING_OPEN_PDF_SUCCESS, state.views.toString());
      break;
    case 'close-pdf':
      state = {...state, pdf: null};
      break;
    case 'open-email':
      if (action.invoices == null) {
        gaAction(gaParams.INVOICE_VIEWING_SEND_INVOICES_NOT_VALID);
      } else {
        if (state.pdf) {
          gaAction(gaParams.INVOICE_VIEWING_CLICK_SEND_INVOICES_PDF);
        } else {
          gaAction(gaParams.INVOICE_VIEWING_CLICK_SEND_INVOICES, state.checked.length.toString());
        }
        gaAction(gaParams.INVOICE_VIEWING_EMAIL_POPUP);
        state = {...state, email: action.invoices};
      }
      break;
    case 'send-email':
      if (action.status == 'SEND') gaAction(gaParams.INVOICE_VIEWING_CLICK_EMAIL_POPUP_SEND, action.count.toString());
      if (action.status == 'SUCCESS') gaAction(gaParams.INVOICE_VIEWING_EMAIL_POPUP_SUCCESS);
      if (action.status == 'FAILURE') gaAction(gaParams.INVOICE_VIEWING_EMAIL_POPUP_FAILURE);
      break;
    case 'close-email':
      state = {...state, email: null};
      break;
    case 'open-timeout':
      gaAction(gaParams.INVOICE_VIEWING_SEARCH_RESULTS_TIMEOUT);
      state = {...state, timeout: true};
      break;
    case 'close-timeout':
      state = {...state, timeout: false};
      break;
    case 'faq':
      switch (action.status) {
        case 'show':
          gaAction(gaParams.INVOICE_VIEWING_CLICK_HELP);
          break;
        case 'item':
          gaAction(gaParams.INVOICE_VIEWING_CLICK_HELP_EXPAND);
          break;
        case 'button':
          gaAction(gaParams.INVOICE_VIEWING_CLICK_HELP_CALL);
          break;
      }
      break;
  }
  if (selected == null && state.selected != null) gaAction(gaParams.INVOICE_VIEWING_SEARCH_APPEAR);
  return state;
};

const BusinessInvoicesContext = createContext<[State, React.Dispatch<Action>]>(null);

export const BuisnessInvoicesProvider: React.FC = ({children}) => {
  const ctx = useReducer(reducer, initialState);
  return <BusinessInvoicesContext.Provider value={ctx}>{children}</BusinessInvoicesContext.Provider>;
};

export default BusinessInvoicesContext;
